// SPDX-License-Identifier: AGPL-3.0-only

export const FULLSCREEN_VERTEX_SHADER = `#version 300 es
precision highp float;
out vec2 v_uv;

void main() {
  vec2 position = gl_VertexID == 0
    ? vec2(-1.0, -1.0)
    : (gl_VertexID == 1 ? vec2(3.0, -1.0) : vec2(-1.0, 3.0));
  v_uv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

export const FLUX_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_terrain;
uniform sampler2D u_state;
uniform sampler2D u_previous_flux;
uniform ivec2 u_size;
uniform ivec4 u_domain;
uniform vec2 u_cell_size;
uniform float u_cell_area;
uniform float u_dt;
uniform float u_gravity;
uniform float u_roughness;
uniform float u_flux_damping;
uniform float u_pipe_area;
uniform float u_minimum_depth;

out vec4 out_flux;

float depthAt(ivec2 p) {
  return max(texelFetch(u_state, clamp(p, ivec2(0), u_size - 1), 0).r, 0.0);
}

float headAt(ivec2 p) {
  ivec2 q = clamp(p, ivec2(0), u_size - 1);
  return texelFetch(u_terrain, q, 0).r + depthAt(q);
}

void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  float depth = depthAt(p);
  if (depth <= 0.0) {
    out_flux = vec4(0.0);
    return;
  }

  float head = headAt(p);
  ivec2 east = p + ivec2(1, 0);
  ivec2 north = p + ivec2(0, -1);
  ivec2 west = p + ivec2(-1, 0);
  ivec2 south = p + ivec2(0, 1);

  float eastDepth = depthAt(east);
  float northDepth = depthAt(north);
  float westDepth = depthAt(west);
  float southDepth = depthAt(south);
  vec4 averageDepth = max(
    vec4(
      0.5 * (depth + eastDepth),
      0.5 * (depth + northDepth),
      0.5 * (depth + westDepth),
      0.5 * (depth + southDepth)
    ),
    vec4(0.0)
  );
  vec4 headDrop = max(
    vec4(head - headAt(east), head - headAt(north), head - headAt(west), head - headAt(south)),
    vec4(0.0)
  );
  vec4 distance = vec4(u_cell_size.x, u_cell_size.y, u_cell_size.x, u_cell_size.y);
  vec4 flowArea = u_pipe_area * averageDepth;
  vec4 previousFlux = max(texelFetch(u_previous_flux, p, 0), vec4(0.0));
  vec4 flux = u_flux_damping * previousFlux +
    u_dt * flowArea * u_gravity * headDrop / max(distance, vec4(0.000001));

  float wetDepth = max(depth, u_minimum_depth);
  float speed = (flux.r + flux.g + flux.b + flux.a) /
    max(u_pipe_area * wetDepth, 0.0001);
  float frictionDecay = 1.0 /
    (1.0 + u_dt * u_gravity * u_roughness * u_roughness * speed /
      pow(wetDepth, 1.3333333));
  flux *= frictionDecay;

  bool atEast = p.x == u_domain.z;
  bool atNorth = p.y == u_domain.y;
  bool atWest = p.x == u_domain.x;
  bool atSouth = p.y == u_domain.w;
  if (atEast) flux.r = 0.0;
  if (atNorth) flux.g = 0.0;
  if (atWest) flux.b = 0.0;
  if (atSouth) flux.a = 0.0;

  float total = flux.r + flux.g + flux.b + flux.a;
  float maximum = depth * u_cell_area / max(u_dt, 0.0001);
  if (total > maximum && total > 0.0) flux *= maximum / total;
  out_flux = clamp(flux, vec4(0.0), vec4(1.0e8));
}
`;

export const UPDATE_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_state;
uniform sampler2D u_flux;
uniform sampler2D u_source_rate;
uniform ivec2 u_size;
uniform ivec4 u_domain;
uniform vec2 u_cell_size;
uniform float u_cell_area;
uniform float u_dt;
uniform float u_maximum_depth;
uniform float u_boundary_drain;

out vec4 out_state;

vec4 fluxAt(ivec2 p) {
  if (p.x < u_domain.x || p.y < u_domain.y || p.x > u_domain.z || p.y > u_domain.w) {
    return vec4(0.0);
  }
  return texelFetch(u_flux, clamp(p, ivec2(0), u_size - 1), 0);
}

void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  float previousDepth = max(texelFetch(u_state, p, 0).r, 0.0);
  vec4 own = max(fluxAt(p), vec4(0.0));
  float outgoing = own.r + own.g + own.b + own.a;
  float incoming = 0.0;
  if (p.x < u_domain.z) incoming += fluxAt(p + ivec2(1, 0)).b;
  if (p.y > u_domain.y) incoming += fluxAt(p + ivec2(0, -1)).a;
  if (p.x > u_domain.x) incoming += fluxAt(p + ivec2(-1, 0)).r;
  if (p.y < u_domain.w) incoming += fluxAt(p + ivec2(0, 1)).g;

  float sourceDepthRate = max(texelFetch(u_source_rate, p, 0).r, 0.0);
  float depth = previousDepth + (incoming - outgoing) * u_dt / u_cell_area + sourceDepthRate * u_dt;
  bool atBoundary = p.x == 0 || p.x == u_size.x - 1 || p.y == 0 || p.y == u_size.y - 1;
  if (atBoundary && u_boundary_drain > 0.0) {
    depth *= exp(-u_boundary_drain * u_dt);
  }
  depth = clamp(depth, 0.0, u_maximum_depth);
  float averageDepth = 0.5 * (previousDepth + depth);
  vec4 westFlux = fluxAt(p + ivec2(-1, 0));
  vec4 eastFlux = fluxAt(p + ivec2(1, 0));
  vec4 northFlux = fluxAt(p + ivec2(0, -1));
  vec4 southFlux = fluxAt(p + ivec2(0, 1));
  float velocityEast = 0.0;
  float velocityNorth = 0.0;
  if (averageDepth > 0.01) {
    velocityEast = ((westFlux.r - own.b) + (own.r - eastFlux.b)) * 0.5 /
      max(averageDepth * u_cell_size.y, 0.0001);
    velocityNorth = ((northFlux.a - own.a) + (own.g - southFlux.g)) * 0.5 /
      max(averageDepth * u_cell_size.x, 0.0001);
  }
  velocityEast = clamp(velocityEast, -30.0, 30.0);
  velocityNorth = clamp(velocityNorth, -30.0, 30.0);
  out_state = vec4(depth, velocityEast, velocityNorth, outgoing);
}
`;
